// Process is provided by webpack DefinePlugin at build time
declare const process: {env: {REACT_APP_AI_GATEWAY_URL?: string}};

import type {ProjectProposal, ReconciliationQuestion} from './types';
import {
	PROJECT_COLOR_AMBER,
	PROJECT_COLOR_PURPLE,
	PROJECT_COLOR_RED,
	PROJECT_COLOR_GREEN,
	PROJECT_COLOR_BLUE,
	PROJECT_COLOR_CORAL,
} from './colors';

export const SimpleUUID = () =>
	typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 11);

// Must match the fallback in _build_sow_proposal (agents/project_planning/reconciliation.py) —
// a placeholder name Nova assigns when no project name was found in the SOW, not a real
// user-entered value. Used to tell a genuinely user-filled name apart from this placeholder.
export const UNNAMED_PROJECT_FALLBACK = 'Unnamed Project';

// True when the user has already supplied a value for this reconciliation question
// through the review form, so the question can be silently auto-skipped instead of
// interrupting the user again. Excludes the "Unnamed Project" placeholder from
// counting as a real name — that value is Nova's own fallback, not user input, so
// treating it as "already filled" would auto-skip the project-name question every time.
export function isReconciliationQuestionAlreadyFilled(q: ReconciliationQuestion, p: ProjectProposal | null): boolean {
	return (
		(q.field_ref === 'extraction_metadata.start_date' && Boolean(p?.start_date)) ||
		(q.field_ref === 'extraction_metadata.end_date' && Boolean(p?.end_date)) ||
		(q.field_ref === 'forecast_client_id' && p?.client_id != null) ||
		(q.field_ref === 'extraction_metadata.project_name' && Boolean(p?.name) && p?.name !== UNNAMED_PROJECT_FALLBACK)
	);
}

export const COLOR_OPTIONS = [
	PROJECT_COLOR_AMBER,
	PROJECT_COLOR_PURPLE,
	PROJECT_COLOR_RED,
	PROJECT_COLOR_GREEN,
	PROJECT_COLOR_BLUE,
	PROJECT_COLOR_CORAL,
];

export const AGENT_URL: string = process.env.REACT_APP_AI_GATEWAY_URL || 'http://localhost:7861';

export const TOOL_LABELS: Record<string, string> = {
	get_current_datetime: 'ai_project_planning.tool_get_current_datetime',
	get_portfolio_health: 'ai_project_planning.tool_get_portfolio_health',
	get_roles: 'ai_project_planning.tool_get_roles',
	get_team_availability: 'ai_project_planning.tool_get_team_availability',
	get_person_utilisation: 'ai_project_planning.tool_get_person_utilisation',
	get_leave: 'ai_project_planning.tool_get_leave',
	create_project: 'ai_project_planning.tool_create_project',
	create_allocation: 'ai_project_planning.tool_create_allocation',
	get_person: 'ai_project_planning.tool_get_person',
	add_project_team_member: 'ai_project_planning.tool_add_project_team_member',
	create_deliverable: 'ai_project_planning.tool_create_deliverable',
	create_baseline_role: 'ai_project_planning.tool_create_baseline_role',
	delete_deliverable: 'ai_project_planning.tool_delete_deliverable',
	delete_project: 'ai_project_planning.tool_delete_project',
	get_client: 'ai_project_planning.tool_get_client',
	get_clients: 'ai_project_planning.tool_get_clients',
	set_project_contact_person: 'ai_project_planning.tool_set_project_contact_person',
	get_rate_cards: 'ai_project_planning.tool_get_rate_cards',
	create_phase: 'ai_project_planning.tool_create_phase',
	link_deliverable_to_phase: 'ai_project_planning.tool_link_deliverable_to_phase',
	create_placeholders_from_baseline: 'ai_project_planning.tool_create_placeholders_from_baseline',
	record_commit_audit: 'ai_project_planning.tool_record_commit_audit',
	__refining__: 'ai_project_planning.tool_refining',
};

export const SUGGESTIONS = [
	'Build an e-commerce platform with a frontend and backend developer over 3 months',
	'Plan a mobile app redesign with a designer and 2 engineers for 6 weeks',
	'Set up a data pipeline project with a data engineer starting next month',
];

export const BUDGET_TYPE_MAP: Record<string, string> = {
	FIXED_PRICE: 'Fixed Price',
	TIME_AND_MATERIALS: 'T&M',
	NON_BILLABLE: 'Non-Billable',
	RETAINER: 'Retainer',
};

export const BUDGET_TYPE_REVERSE_MAP: Record<string, string> = {
	'Fixed Price': 'FIXED_PRICE',
	'T&M': 'TIME_AND_MATERIALS',
	'Non-Billable': 'NON_BILLABLE',
	Retainer: 'RETAINER',
};

// invoice_type is a backend enum ("DEPOSIT" | "FIXED_PRICE" | "TIME_AND_MATERIAL") and must
// never be shown to the user untranslated. Maps each value to a translation message id,
// mirroring how the budget-type pills look up their own label ids.
export const INVOICE_TYPE_LABEL_IDS: Record<string, string> = {
	DEPOSIT: 'sow_review.invoice_type_deposit',
	FIXED_PRICE: 'sow_review.invoice_type_fixed_price',
	TIME_AND_MATERIAL: 'sow_review.invoice_type_time_and_material',
};

// The full set of invoice_type values a manually-added invoice's type dropdown can offer —
// mirrors create_draft_invoice's own _VALID_INVOICE_TYPES (Forecast-MCP), since sending
// anything else is rejected there.
export const INVOICE_TYPE_OPTIONS = ['DEPOSIT', 'FIXED_PRICE', 'TIME_AND_MATERIAL'] as const;

// Default invoice_type for a manually-added invoice row, derived from the project's own
// budget_type rather than a hardcoded value. Not a direct copy: budget_type's
// TIME_AND_MATERIALS (plural) and invoice_type's TIME_AND_MATERIAL (singular) are distinct
// enums, and not every budget_type has an invoicing equivalent (e.g. NON_BILLABLE) — those
// fall back to FIXED_PRICE, the same default every manually-added invoice already got before
// this mapping existed. The dropdown stays editable either way; this only seeds its initial
// value once, when the row is created.
export function deriveInvoiceTypeFromBudgetType(budgetType: string | null | undefined): string {
	if (budgetType === 'TIME_AND_MATERIALS') return 'TIME_AND_MATERIAL';
	return 'FIXED_PRICE';
}

export function getAvatarColor(name: string): string {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return COLOR_OPTIONS[Math.abs(hash) % COLOR_OPTIONS.length];
}

export function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return (parts[0] || '?')[0].toUpperCase();
}

// Step names MUST match adispatch_custom_event("step_started", {"step": "..."}) in nodes.py extract_sow()
export const SOW_CHECKLIST_STEPS: string[] = [
	'Extracting project name & client',
	'Extracting dates & duration',
	'Extracting deliverables',
	'Extracting budget & rates',
	'Extracting suggested team',
];

// Derives which fields Nova likely filled from proposal values, used as a fallback
// when the backend omits nova_filled_fields.
export function deriveNovaFilledFields(p: ProjectProposal): string[] {
	const derived: string[] = [];
	if (p.name) derived.push('name');
	if (p.client_id != null) derived.push('client_id');
	if (p.start_date) derived.push('start_date');
	if (p.end_date) derived.push('end_date');
	if (p.budget != null) derived.push('budget');
	if (p.budget_type) derived.push('budget_type');
	if ((p.deliverables?.length ?? 0) > 0) derived.push('deliverables');
	if ((p.invoices?.length ?? 0) > 0) derived.push('invoices');
	return derived;
}

export const SOW_CHECKLIST_LABELS: Record<string, string> = {
	'Extracting project name & client': 'Project name & client',
	'Extracting dates & duration': 'Dates & duration',
	'Extracting deliverables': 'Deliverables',
	'Extracting budget & rates': 'Budget & rates',
	'Extracting suggested team': 'Suggested team',
};
